<template>
	<div id="yearEndDistributionTable">
		<div class="container scroll-fade" :class="{ '--show-gradient': showGradient }">
			<TableFixedLeft @willScroll="(result) => (showGradient = result)">
				<TableStickyHeader :headerHeight="headerHeight" headerBgColor="#f5f5f5">
					<table class="ye-dist-table">
						<thead>
							<tr>
								<th scope="col" class="fund-name-column lg-3">
									<button
										class="button--simple"
										@click="sortColumn('fundName')"
										ref="fundNameColumn"
										:aria-label="`${tableHeaderContent.labels.fundName} ascending`"
										aria-live="polite"
									>
										<span class="eyebrow no-wrap">{{ tableHeaderContent.labels.fundName }}</span>
										<span
											class="icon"
											:class="sortableCols.fundName.currentSortState === 'ascending' ? 'icon-down' : 'icon-up'"
											aria-hidden="true"
										></span>
									</button>
								</th>
								<th scope="col" class="symbol-column lg-1">
									<span class="colgroup-start" />
									<button
										class="button--simple"
										@click="sortColumn('symbol')"
										ref="symbolColumn"
										:aria-label="`${tableHeaderContent.labels.symbol} ascending`"
										aria-live="polite"
									>
										<span class="eyebrow no-wrap">{{ tableHeaderContent.labels.symbol }}</span>
										<span
											class="icon"
											:class="sortableCols.symbol.currentSortState === 'ascending' ? 'icon-down' : 'icon-up'"
											aria-hidden="true"
										></span>
									</button>
								</th>
								<th scope="col" class="lg-1">
									<span class="eyebrow">{{ tableHeaderContent.labels.distributionType }}</span>
								</th>
								<th scope="col" class="lg-1">
									<span class="eyebrow">{{ tableHeaderContent.labels.mostRecentDistribution }}</span>
								</th>
								<th scope="col" class="lg-1">
									<ToolTip
										class="eyebrow"
										:text="tableHeaderContent.labels.exDividendDate"
										:message="tableHeaderContent.toolTipDefinitions.exDividendDate"
										:openModalInSmall="true"
									></ToolTip>
								</th>
								<th scope="col" class="lg-1">
									<ToolTip
										class="eyebrow"
										:text="tableHeaderContent.labels.recordDate"
										:message="tableHeaderContent.toolTipDefinitions.recordDate"
										:openModalInSmall="true"
									></ToolTip>
								</th>
								<th scope="col" class="lg-1">
									<ToolTip
										class="eyebrow"
										:text="tableHeaderContent.labels.reinvestDate"
										:message="tableHeaderContent.toolTipDefinitions.reinvestDate"
										:openModalInSmall="true"
									></ToolTip>
								</th>
								<th scope="col" class="lg-1">
									<ToolTip
										class="eyebrow"
										:text="tableHeaderContent.labels.payableDate"
										:message="tableHeaderContent.toolTipDefinitions.payableDate"
										:openModalInSmall="true"
									></ToolTip>
								</th>
								<th scope="col" class="lg-1">
									<span class="eyebrow">{{ tableHeaderContent.labels.reinvestPrice }}</span>
								</th>
								<th scope="col" class="lg-1">
									<ToolTip
										class="eyebrow"
										:text="tableHeaderContent.labels.distributionYield"
										:message="tableHeaderContent.toolTipDefinitions.distributionYield"
										:openModalInSmall="true"
									></ToolTip>
								</th>
								<th scope="col" class="lg-1">
									<ToolTip
										class="eyebrow"
										:text="tableHeaderContent.labels.secYield"
										:message="tableHeaderContent.toolTipDefinitions.secYield"
										:openModalInSmall="true"
									></ToolTip>
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(fund, rowIndex) in paginatedFundData" :key="rowIndex">
								<th v-html="createFundNameLink(fund)" class="fund-name-column"></th>
								<td class="symbol-column">{{ fund.ticker | emDashNull }}</td>
								<td>{{ fund.distributionType | emDashNull }}</td>
								<td>{{ fund.mostRecentDistribution | toUSD(5) | emDashNull }}</td>
								<td>{{ fund.exDividendDate | formattedDate | emDashNull }}</td>
								<td>{{ fund.recordDate | formattedDate | emDashNull }}</td>
								<td>{{ fund.reinvestDate | formattedDate | emDashNull }}</td>
								<td>{{ fund.payableDate | formattedDate | emDashNull }}</td>
								<td>{{ fund.reinvestPrice | toUSD | emDashNull }}</td>
								<td>{{ fund.distributionYield | toFixed(2) | append("%") | emDashNull }}</td>
								<td class="no-wrap">
									<span
										v-if="isSecYieldAndFootnoteAvailable(fund.secYield, fund.secYieldFootnote, fund.secYieldFootnote2)"
									>
										{{ fund.secYield | toFixed(2) | append("%") }}
										<ToolTip
											class="eyebrow"
											:text="buildSecYieldCode(fund.secYieldFootnote, fund.secYieldFootnote2)"
											:message="
												buildSecYieldDescription(
													fund.secYieldFootnote,
													fund.secYieldFootnote2,
													fund.isInflationProtectedSecurity
												)
											"
											:openModalInSmall="true"
										></ToolTip>
									</span>
									<span v-else>{{ undefined | emDashNull }}</span>
								</td>
							</tr>
						</tbody>
					</table>
				</TableStickyHeader>
			</TableFixedLeft>
		</div>
		<Pagination :pageSize="rowCountPerPage" :data="fundDataArray" @pagination:change="updateData" />
	</div>
</template>

<script>
import Pagination from "@vg-faset/ui/src/components/pagination/Pagination.vue";
import Vue2Filters from "vue2-filters";
import ToolTip from "@vg-faset/ui/src/components/tooltip/Tooltip.vue";
import TableFixedLeft from "@vg-faset/ui/src/components/table-fixed-left/TableFixedLeft.vue";
import TableStickyHeader from "@vg-faset/ui/src/components/table-sticky-header/TableStickyHeader.vue";
import { emDashNull } from "@vg-faset/core/js/filters/string/index";

export default {
	name: "YearEndDistributionsTable",
	mixins: [Vue2Filters.mixin],
	components: {
		Pagination,
		TableFixedLeft,
		TableStickyHeader,
		ToolTip
	},
	props: {
		fundData: {
			type: Array,
			default: () => []
		},
		tableHeaderContent: {
			type: Object,
			default: () => ({
				labels: {},
				toolTipDefinitions: {}
			})
		}
	},
	data() {
		return {
			fundDataArray: [],
			rowCountPerPage: 10,
			fundTableData: [],
			headerHeight: {
				md: 50,
				lg: 120
			},
			sortableCols: {
				fundName: {
					currentSortState: "ascending",
					propertyToSortBy: "name"
				},
				symbol: {
					currentSortState: "ascending",
					propertyToSortBy: "ticker"
				}
			},
			showGradient: false
		};
	},
	mounted() {
		this.fundDataArray = this.fundData;
	},
	computed: {
		paginatedFundData() {
			return this.fundTableData;
		}
	},
	methods: {
		updateData(filteredRows) {
			this.fundTableData = filteredRows;
		},
		createFundNameLink(fund) {
			let fundNameTableData;

			if (fund.name) {
				let fundIdentifier = fund.ticker ? fund.ticker : fund.fundId;
				fundNameTableData = `<a href="/investments/products/${fundIdentifier}">${fund.name}</a>`;
			}

			return emDashNull(fundNameTableData);
		},
		sortColumn(colName) {
			let sortableCol = this.sortableCols[colName];
			let sortButton = colName === "fundName" ? this.$refs.fundNameColumn : this.$refs.symbolColumn;
			let sortButtonName = sortButton?.textContent.trim();

			if (sortableCol.currentSortState === "ascending") {
				this.fundDataArray = this.orderBy(this.fundDataArray, sortableCol.propertyToSortBy, -1);
				sortableCol.currentSortState = "descending";
				sortButton?.setAttribute("aria-label", sortButtonName + " descending");
			} else {
				this.fundDataArray = this.orderBy(this.fundDataArray, sortableCol.propertyToSortBy);
				sortableCol.currentSortState = "ascending";
				sortButton?.setAttribute("aria-label", sortButtonName + " ascending");
			}
		},
		buildSecYieldCode(footnote, footnote2) {
			let yieldCode = footnote.code;

			if (footnote2 && footnote2.code && footnote2.description) {
				yieldCode += "," + footnote2.code;
			}

			return yieldCode;
		},
		buildSecYieldDescription(footnote, footnote2, isInflationProtectedSecurity) {
			let yieldDescription = "";

			if (isInflationProtectedSecurity) {
				yieldDescription = this.tableHeaderContent.toolTipDefinitions.inflationProtectedSecurities + "<br>";
			}

			if (footnote2 && footnote2.code && footnote2.description) {
				yieldDescription += footnote.description + "<br><br>" + footnote2.description;
			} else {
				yieldDescription += footnote.description;
			}

			return yieldDescription;
		},
		isSecYieldAndFootnoteAvailable(secYield, secYieldFootnote, secYieldFootnote2) {
			let isSecYieldAndFootnoteDataAvailable = false;

			if (secYield && secYieldFootnote && secYieldFootnote.code && secYieldFootnote.description) {
				if (secYieldFootnote2) {
					if (secYieldFootnote2.code && secYieldFootnote2.description) {
						isSecYieldAndFootnoteDataAvailable = true;
					}
				} else {
					isSecYieldAndFootnoteDataAvailable = true;
				}
			}

			return isSecYieldAndFootnoteDataAvailable;
		}
	},
	watch: {
		fundData(newVal) {
			this.fundDataArray = newVal;
		}
	}
};
</script>

<style lang="scss">
#yearEndDistributionTable {
	padding-bottom: 50px;
}

.ye-dist-table {
	.button--simple .icon {
		font-size: 8px;
		margin-left: 8px;
		top: -2px;
		position: relative;
		display: inline;
	}

	.colgroup-start {
		height: 120px;
	}

	.eyebrow {
		text-transform: none;
		font-size: 12px;
	}

	td {
		font-size: 15px;
	}

	thead {
		font-weight: $font-weight--bold;
	}

	thead th:first-of-type {
		background-color: $color--neutral-9;
		background-clip: padding-box;
	}

	thead th {
		background-color: $color--neutral-9;
		vertical-align: bottom;
		padding-bottom: 20px;
		font-size: 12px;
		line-height: 18px;
		padding-top: 42px;
		position: relative;
	}

	tbody {
		th {
			a {
				color: $color--black;
				font-weight: bold;
				font-size: 15px;
			}

			a:hover {
				text-decoration: underline;
			}
		}

		tr {
			height: 112px;
		}
	}

	.fund-name-column {
		padding-left: 16px;

		@include at-md {
			min-width: 168px;
		}
	}

	.symbol-column {
		padding-left: 40px;
		white-space: nowrap;
	}
}
</style>
